import React, { useEffect, useState, useRef } from 'react';
import axios from '../axiosInstance';
import {
  FiSearch,
  FiAlertCircle,
  FiBox,
  FiChevronLeft,
  FiChevronRight,
  FiDatabase,
  FiX,
  FiCheck,
  FiLoader,
} from 'react-icons/fi';

export default function GetStock() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [role, setRole] = useState('');

  // Unblock quantity state
  const [unblockQuantities, setUnblockQuantities] = useState({});
  const [unblockingId, setUnblockingId] = useState(null);
  const [unblockSuccess, setUnblockSuccess] = useState('');

  const [filters, setFilters] = useState({
    state_id: '',
    purpose_id: '',
    warehouse_id: '',
    commodity_id: '',
    scheme_id: '',
  });

  const [states, setStates] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [schemes, setSchemes] = useState([]);

  const token = localStorage.getItem('token');
  const fetchDataRef = useRef(null);
  const listsLoadedRef = useRef(false);
  const initialLoadRef = useRef(false);

  // Dropdown controls
  const [dropdownOpen, setDropdownOpen] = useState({
    state: false,
    purpose: false,
    warehouse: false,
    commodity: false,
    scheme: false,
  });
  const [search, setSearch] = useState({
    state: '',
    purpose: '',
    warehouse: '',
    commodity: '',
    scheme: '',
  });

  useEffect(() => {
    if (listsLoadedRef.current) return;
    listsLoadedRef.current = true;
    const fetchLists = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        const userRole = user?.roles?.[0]?.name || '';
        setRole(userRole);

        const [
          statesRes,
          purposesRes,
          warehousesRes,
          commoditiesRes,
          schemesRes,
        ] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BASE_URL}states`),
          axios.get(`${process.env.REACT_APP_BASE_URL}purposes?paginate=false`),
          axios.get(
            `${process.env.REACT_APP_BASE_URL}warehouse?paginate=false&has_inventory=true`
          ),
          axios.get(`${process.env.REACT_APP_BASE_URL}commodity`),
          axios.get(`${process.env.REACT_APP_BASE_URL}schemes`),
        ]);
        setStates(statesRes.data || []);
        setPurposes(purposesRes.data || []);
        setWarehouses(warehousesRes.data || []);
        setCommodities(commoditiesRes.data || []);
        setSchemes(schemesRes.data || []);
      } catch {
        setError('Failed to load filter lists. Please refresh.');
      }
    };
    fetchLists();
  }, []);

  const fetchStockBlocking = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      Object.entries(filters).forEach(([key, val]) => {
        if (val) params.append(key, val);
      });

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}stock-blocking?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setData(response.data.data || []);
      const returnedTotal = response.data.total || 0;
      setTotal(returnedTotal);
      setTotalPages(Math.ceil(returnedTotal / limit));
    } catch (err) {
      let msg = 'Failed to fetch data';
      if (err.response?.data?.message) msg = err.response.data.message;
      else if (err.message) msg = err.message;
      setError(msg);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      return;
    }
    if (fetchDataRef.current) clearTimeout(fetchDataRef.current);
    fetchDataRef.current = setTimeout(() => {
      fetchStockBlocking();
    }, 300);
    return () => {
      if (fetchDataRef.current) clearTimeout(fetchDataRef.current);
    };
    // eslint-disable-next-line
  }, [page, limit, filters]);

  const handleSelect = (name, value, labelKey = 'name') => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setDropdownOpen((prev) => ({ ...prev, [name]: false }));
    setPage(1);
  };

  const clearFilter = (name) => {
    setFilters((prev) => ({ ...prev, [name]: '' }));
    setSearch((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSearch = () => {
    fetchStockBlocking();
  };

  // Handle unblock quantity change
  // Handle unblock input change
  const handleUnblockChange = (blockId, field, value) => {
    setUnblockQuantities((prev) => ({
      ...prev,
      [blockId]: {
        ...prev[blockId],
        [field]: value
      },
    }));
  };

  const [selectedRows, setSelectedRows] = useState(new Set());

  // Toggle single row selection
  const handleSelectRow = (id) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Toggle select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = data.map((row) => row.id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  // Calculate total selected blocked quantity
  const calculateTotalSelectedQty = () => {
    return data
      .filter((row) => selectedRows.has(row.id))
      .reduce((sum, row) => sum + parseFloat(row.blocked_qty || 0), 0)
      .toFixed(2);
  };

  const calculateTotalSelectedBags = () => {
    return data
      .filter((row) => selectedRows.has(row.id))
      .reduce((sum, row) => sum + parseFloat(row.bags || 0), 0)
      .toFixed(0);
  };

  // Bulk Unblock State
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);

  // Handle Bulk Unblock
  const handleBulkUnblock = async () => {
    if (selectedRows.size === 0) return;

    setBulkLoading(true);
    setError('');

    try {
      const whrIds = data
        .filter(row => selectedRows.has(row.id))
        .map(row => row.whr_id);

      const payload = {
        whr_ids: whrIds
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}stock-blocking/unblock/bulk`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setBulkResults(response.data.results);
      setShowBulkModal(true);

      if (response.data.results?.success?.length > 0) {
        fetchStockBlocking();
        setSelectedRows(new Set());
      }

    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        'Failed to process bulk unblocking'
      );
    } finally {
      setBulkLoading(false);
    }
  };

  const handleUnblockStock = async (blockId) => {
    const unblockQty = unblockQuantities[blockId];

    if (!unblockQty || parseFloat(unblockQty) <= 0) {
      setError('Please enter a valid unblock quantity');
      return;
    }

    setUnblockingId(blockId);
    setError('');
    setUnblockSuccess('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}stock-blocking/unblock`,
        {
          block_id: blockId,
          unblock_qty: parseFloat(unblockQty?.unblock_qty || 0),
          unblock_bags: parseFloat(unblockQty?.unblock_bags || 0),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setUnblockSuccess(response.data?.message || 'Stock unblocked successfully');
      setUnblockQuantities((prev) => {
        const newState = { ...prev };
        delete newState[blockId];
        return newState;
      });

      // Refresh the data after successful unblock
      setTimeout(() => {
        fetchStockBlocking();
        setUnblockSuccess('');
      }, 1500);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to unblock stock';
      setError(errorMsg);
    } finally {
      setUnblockingId(null);
    }
  };

  const renderDropdown = (label, name, items, labelKey) => {
    const isOpen = dropdownOpen[name];
    const selectedValue = filters[name];
    const searchTerm = search[name];

    return (
      <div className="relative w-[250px] max-w-full">
        <div
          onClick={() =>
            setDropdownOpen((prev) => {
              // Close all dropdowns first, then toggle the clicked one
              const allClosed = Object.keys(prev).reduce((acc, key) => {
                acc[key] = false;
                return acc;
              }, {});
              return { ...allClosed, [name]: !prev[name] };
            })
          }
          className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px]"
        >
          <span>
            {selectedValue
              ? searchTerm ||
              items.find((i) => i.id === selectedValue)?.[labelKey] ||
              `${label} Selected`
              : `Select ${label}`}
          </span>
          <div className="flex items-center gap-2">
            {selectedValue && (
              <FiX
                onClick={(e) => {
                  e.stopPropagation();
                  clearFilter(name);
                }}
                className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                title="Clear"
              />
            )}
            <span className="text-gray-500">{isOpen ? '▲' : '▼'}</span>
          </div>
        </div>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
            <input
              type="text"
              placeholder={`Search ${label}...`}
              value={searchTerm}
              onChange={(e) =>
                setSearch((prev) => ({ ...prev, [name]: e.target.value }))
              }
              className="w-full px-3 py-2 border-b text-sm focus:outline-none"
            />
            {items
              .filter((i) => {
                const label = i && i[labelKey] ? String(i[labelKey]) : '';
                return label
                  .toLowerCase()
                  .includes((searchTerm || '').toLowerCase());
              })
              .map((i) => (
                <div
                  key={i.id}
                  onClick={() => handleSelect(name, i.id, labelKey)}
                  className={`p-2 cursor-pointer hover:bg-blue-100 ${filters[name] === i.id ? 'bg-blue-200 font-semibold' : ''
                    }`}
                >
                  {i[labelKey]}
                </div>
              ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100">
      <div className="flex items-center gap-3 mb-6">
        <FiDatabase className="text-3xl text-blue-700" />
        <h2 className="text-2xl md:text-3xl font-extrabold text-blue-800 tracking-tight">
          Stock Blocking List
        </h2>
      </div>

      {/* Search Filters */}
      <div className="flex flex-wrap gap-4 mb-6 items-center justify-start">
        {renderDropdown('State', 'state_id', states, 'name')}
        {renderDropdown(
          'Commodity',
          'commodity_id',
          commodities,
          'commodityname'
        )}
        {renderDropdown('Scheme', 'scheme_id', schemes, 'name')}
        {renderDropdown('Purpose', 'purpose_id', purposes, 'name')}
        {renderDropdown(
          'Warehouse',
          'warehouse_id',
          warehouses,
          'warehouse_name'
        )}

        <button
          onClick={handleSearch}
          className="w-[250px] h-[50px] bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 shadow-sm transition"
        >
          <FiSearch className="text-lg" /> Search
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 flex items-center gap-2 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg text-base font-semibold">
          <FiAlertCircle className="text-xl" />
          {error}
        </div>
      )}

      {/* Success Message */}
      {unblockSuccess && (
        <div className="mb-6 flex items-center gap-2 p-4 bg-green-100 border border-green-300 text-green-700 rounded-lg text-base font-semibold">
          <FiCheck className="text-xl" />
          {unblockSuccess}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl shadow-xl border border-gray-100 bg-white">
        <table className="w-full text-left border-collapse min-w-[1100px]">
          <thead className="bg-gray-100 border-b border-gray-200">
            <tr>
              {role !== 'User' && (
                <th className="py-4 px-6 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={data.length > 0 && selectedRows.size === data.length}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                </th>
              )}
              {[
                'WHR ID',
                'State',
                'Warehouse',
                'Commodity',
                'Scheme',
                'Quantity (MT)',
                'Bags (Nos)',
                'Purpose',
                'Status',
                role !== 'User' && 'Action',
              ].filter(Boolean).map((header) => (
                <th
                  key={header}
                  className="py-4 px-6 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td
                  colSpan={10}
                  className="text-center py-10 text-blue-600 text-lg font-semibold"
                >
                  <div className="flex justify-center items-center gap-2">
                    <FiLoader className="animate-spin text-2xl" />
                    <span>Loading...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="text-center py-12 text-gray-500 font-medium text-lg"
                >
                  <div className="flex flex-col items-center gap-2">
                    <FiAlertCircle className="text-3xl text-gray-400" />
                    <span>No data found. Please select filters and click Search.</span>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-gray-50 transition-colors duration-200"
                >
                  {role !== 'User' && (
                    <td className="py-4 px-6">
                      <input
                        type="checkbox"
                        checked={selectedRows.has(item.id)}
                        onChange={() => handleSelectRow(item.id)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </td>
                  )}
                  <td className="py-4 px-6 text-sm font-semibold text-gray-900">{item.whr_id}</td>
                  <td className="py-4 px-6 text-sm font-semibold text-gray-900">{item.state?.name}</td>
                  <td className="py-4 px-6 text-sm text-gray-700">
                    <div className="flex flex-col">
                      <span className="font-semibold text-gray-900">
                        {item.warehouse?.warehouse_name}
                      </span>
                      <span className="text-sm text-gray-500">
                        {item.warehouse?.warehouse_code}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-sm text-blue-700 font-medium">{item.commodity?.commodityname}</td>
                  <td className="py-4 px-6 text-sm text-blue-700 font-medium">{item.scheme?.name}</td>
                  <td className="py-4 px-6 text-sm">
                    <div className="flex flex-col">
                      <span className="block font-bold text-gray-900" title="Actual">
                        Actual: {item.actual_qty}
                      </span>
                      <span className="block text-xs font-semibold text-red-600 bg-red-50 px-1 py-0.5 rounded border border-red-100 mt-1 w-fit" title="Blocked">
                        Blocked: {item.blocked_qty}
                      </span>
                      <span className="block text-xs font-semibold text-blue-600 bg-blue-50 px-1 py-0.5 rounded border border-blue-100 mt-1 w-fit" title="Current">
                        Current: {item.remaining_qty}
                      </span>
                      <span className="block text-xs font-semibold text-gray-600 bg-gray-50 px-1 py-0.5 rounded border border-gray-100 mt-1 w-fit" title="Withdrawal">
                        Withdrawal: {item.withdrawal_qty}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-sm">
                    <div className="flex flex-col">
                      <span className="block font-bold text-gray-900" title="Actual">
                        Actual: {item.actual_bags}
                      </span>
                      <span className="block text-xs font-semibold text-red-600 bg-red-50 px-1 py-0.5 rounded border border-red-100 mt-1 w-fit" title="Blocked">
                        Blocked: {item.blocked_bags}
                      </span>
                      <span className="block text-xs font-semibold text-blue-600 bg-blue-50 px-1 py-0.5 rounded border border-blue-100 mt-1 w-fit" title="Current">
                        Current: {item.current_bags}
                      </span>
                      <span className="block text-xs font-semibold text-gray-600 bg-gray-50 px-1 py-0.5 rounded border border-gray-100 mt-1 w-fit" title="Withdrawal">
                        Withdrawal: {item.withdrawal_bags}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-sm text-purple-700 font-medium">{item.purpose?.name}</td>

                  <td className="py-4 px-6 text-sm text-green-700 font-medium">{item.status}</td>
                  {role !== 'User' && (
                    <td className="py-4 px-6 text-sm">
                      <div className="flex gap-2 items-center">
                        <input
                          type="number"
                          placeholder="Qty"
                          value={unblockQuantities[item.id]?.unblock_qty || ''}
                          onChange={(e) =>
                            handleUnblockChange(item.id, 'unblock_qty', e.target.value)
                          }
                          className={`w-24 px-3 py-1.5 border rounded-lg text-sm
               focus:ring-2 outline-none transition bg-gray-50 ${(parseFloat(unblockQuantities[item.id]?.unblock_qty || 0) > parseFloat(item.blocked_qty || 0))
                              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                              : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                            }`}
                          step="0.1"
                          min="0"
                          disabled={
                            item.status === 'RELEASED' || unblockingId === item.id
                          }
                        />
                        <input
                          type="number"
                          placeholder="Bags"
                          value={unblockQuantities[item.id]?.unblock_bags || ''}
                          onChange={(e) =>
                            handleUnblockChange(item.id, 'unblock_bags', e.target.value)
                          }
                          className={`w-24 px-3 py-1.5 border rounded-lg text-sm
               focus:ring-2 outline-none transition bg-gray-50 ${(parseFloat(unblockQuantities[item.id]?.unblock_bags || 0) > parseFloat(item.bags || 0))
                              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                              : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                            }`}
                          step="1"
                          min="0"
                          disabled={
                            item.status === 'RELEASED' || unblockingId === item.id
                          }
                        />
                        <button
                          onClick={() => handleUnblockStock(item.id)}
                          className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600
               disabled:opacity-40 disabled:cursor-not-allowed
               text-white rounded-lg text-xs font-semibold
               uppercase tracking-wide flex items-center gap-1
               transition shadow-sm"
                          disabled={
                            item.status === 'RELEASED' ||
                            unblockingId === item.id ||
                            !unblockQuantities[item.id]?.unblock_qty ||
                            parseFloat(unblockQuantities[item.id]?.unblock_qty || 0) > parseFloat(item.blocked_qty || 0) ||
                            parseFloat(unblockQuantities[item.id]?.unblock_bags || 0) > parseFloat(item.bags || 0)
                          }
                        >
                          {item.status === 'RELEASED' ? (
                            'Unblock'
                          ) : unblockingId === item.id ? (
                            <>
                              <FiLoader className="animate-spin" size={14} />
                              Unblocking...
                            </>
                          ) : (
                            <>
                              <FiCheck size={14} />
                              Unblock
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data.length > 0 && (
        <div className="mt-8 flex flex-col md:flex-row justify-between items-center gap-4 p-4 bg-white rounded-xl shadow-md border border-gray-100">
          {/* Record Summary */}
          <div className="text-sm text-gray-600 font-medium order-2 md:order-1">
            Showing <span className="text-blue-600 font-bold">{Math.min(total, (page - 1) * limit + 1)}</span> to{' '}
            <span className="text-blue-600 font-bold">{Math.min(total, page * limit)}</span> of{' '}
            <span className="text-blue-600 font-bold">{total}</span> records
          </div>

          {/* Page Controls */}
          <div className="flex items-center gap-4 order-1 md:order-2">
            <div className="flex items-center gap-2">
              <button
                disabled={page === 1 || loading}
                onClick={() => setPage(page - 1)}
                className="flex items-center gap-1 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition font-bold"
              >
                <FiChevronLeft className="text-lg" /> Prev
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-10 h-10 flex items-center justify-center rounded-lg font-bold transition ${page === pageNum
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {totalPages > 5 && page < totalPages - 2 && (
                  <span className="px-2 text-gray-400">...</span>
                )}
                {totalPages > 5 && page < totalPages - 2 && (
                  <button
                    onClick={() => setPage(totalPages)}
                    className="w-10 h-10 flex items-center justify-center rounded-lg font-bold bg-gray-50 text-gray-600 hover:bg-gray-200"
                  >
                    {totalPages}
                  </button>
                )}
              </div>

              <button
                disabled={page === totalPages || loading}
                onClick={() => setPage(page + 1)}
                className="flex items-center gap-1 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition font-bold"
              >
                Next <FiChevronRight className="text-lg" />
              </button>
            </div>
          </div>

          {/* Page Info */}
          <div className="hidden md:block text-sm font-semibold text-gray-500 order-3">
            Page <span className="text-blue-600">{page}</span> of {totalPages}
          </div>
        </div>
      )}
      {/* Footer Summary Bar */}
      {selectedRows.size > 0 && role !== 'User' && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white border border-gray-200 shadow-2xl rounded-xl p-4 z-50 min-w-[600px] animate-fade-in-up">
          <div className="flex justify-between items-center gap-8 px-2">
            <div className="flex items-center gap-3">
              <span className="bg-blue-600 text-white w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold shadow-md">
                {selectedRows.size}
              </span>
              <span className="text-gray-700 font-semibold text-lg">Items Selected</span>
            </div>

            <div className="flex items-center gap-6">
              <div className="h-10 w-px bg-gray-200"></div>
              <div className="text-right">
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-0.5">Total Blocked Qty</p>
                <p className="text-xl font-black text-gray-800 leading-none">
                  {calculateTotalSelectedQty()} <span className="text-sm font-medium text-gray-400">MT</span>
                </p>
              </div>

              <div className="text-right border-l pl-6 border-gray-200">
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-0.5">Total Bags</p>
                <p className="text-xl font-black text-gray-800 leading-none">
                  {calculateTotalSelectedBags()} <span className="text-sm font-medium text-gray-400">Nos</span>
                </p>
              </div>

              <button
                className="px-6 py-2.5 bg-gray-900 hover:bg-black text-white font-bold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                onClick={handleBulkUnblock}
                disabled={bulkLoading}
              >
                {bulkLoading ? (
                  <>
                    <FiLoader className="animate-spin text-lg" /> Processing...
                  </>
                ) : (
                  <>
                    <FiCheck className="text-lg" /> Unblock Selected
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Results Modal */}
      {showBulkModal && bulkResults && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col animate-fade-in-up">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="text-xl font-bold text-gray-800">Unblock Stock Results</h3>
              <button
                onClick={() => setShowBulkModal(false)}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
              >
                <FiX className="text-gray-500 text-xl" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              {/* Success Section */}
              {bulkResults.success?.length > 0 && (
                <div className="mb-6">
                  <h4 className="flex items-center gap-2 text-green-700 font-bold mb-3">
                    <FiCheck className="text-xl" />
                    Successfully Unblocked ({bulkResults.success.length})
                  </h4>
                  <div className="bg-green-50 rounded-xl border border-green-100 overflow-hidden">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-green-100/50 text-green-800 font-semibold">
                        <tr>
                          <th className="px-4 py-2">WHR ID</th>
                          <th className="px-4 py-2">Message</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-green-100">
                        {bulkResults.success.map((item, idx) => (
                          <tr key={idx} className="hover:bg-green-100/30">
                            <td className="px-4 py-2 font-medium">{item.whr_id}</td>
                            <td className="px-4 py-2">{item.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Failed Section */}
              {bulkResults.failed?.length > 0 && (
                <div>
                  <h4 className="flex items-center gap-2 text-red-700 font-bold mb-3">
                    <FiAlertCircle className="text-xl" />
                    Failed to Unblock ({bulkResults.failed.length})
                  </h4>
                  <div className="bg-red-50 rounded-xl border border-red-100 overflow-hidden">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-red-100/50 text-red-800 font-semibold">
                        <tr>
                          <th className="px-4 py-2">WHR ID</th>
                          <th className="px-4 py-2">Error</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-red-100">
                        {bulkResults.failed.map((item, idx) => (
                          <tr key={idx} className="hover:bg-red-100/30">
                            <td className="px-4 py-2 font-medium">{item.whr_id}</td>
                            <td className="px-4 py-2">{item.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end">
              <button
                onClick={() => setShowBulkModal(false)}
                className="px-6 py-2 bg-gray-800 hover:bg-gray-900 text-white font-semibold rounded-lg shadow-sm transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
